import React, { useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  Tooltip,
  Fade,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import {
  AccountCircle as AccountCircleIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Category as CategoryIcon,
  CameraAlt as CameraIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from "@mui/icons-material";

export default function TopAppBar({ darkMode, setDarkMode }) {
  const history = useHistory();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 900px)");

  // Get user display name
  const username =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username || user.email || "User";

  // Get organization name
  let organizationName = "";
  if (user.organization) {
    if (typeof user.organization === "object") {
      organizationName = user.organization.name || "";
    } else {
      organizationName = user.organization;
    }
  }

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setNotificationsAnchor(null);
  };

  const handleAdminClick = () => {
    window.location = "https://api.tpilums.org.pk/admin/";
    handleClose();
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleClose();
    setMobileDrawerOpen(false);
  };

  const handleProfile = () => {
    history.push("/profile");
    handleClose();
  };

  const handleChangePassword = () => {
    history.push("/change-password");
    handleClose();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_id");
    history.push("/logout");
    handleClose();
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: "/", label: "Home", icon: <HomeIcon /> },
    { path: "/dashboard", label: "Dashboard", icon: <DashboardIcon /> },
    { path: "/cameras", label: "Cameras", icon: <CameraIcon /> },
    { path: "/species", label: "Species", icon: <CategoryIcon /> },
  ];

  // Gradient styles (matching Forest Fire project)
  const gradientBar = {
    background:
      "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    backdropFilter: "blur(10px)",
  };

  const iconButtonStyle = {
    borderRadius: "50%",
    padding: "10px",
    transition: "all 0.3s ease",
    color: "white",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
      transform: "scale(1.05)",
    },
  };

  const menuPaperStyle = {
    borderRadius: "16px",
    minWidth: "220px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
    background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
    overflow: "hidden",
  };

  const menuItemStyle = {
    py: 1.5,
    px: 2.5,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f0f0f0",
      transform: "translateX(4px)",
    },
  };

  const mobileDrawerStyle = {
    width: "280px",
    background: "linear-gradient(180deg, #454545 0%, #2d2d2d 100%)",
    color: "white",
  };

  return (
    <>
      <AppBar position="fixed" sx={gradientBar} elevation={0}>
        <Toolbar
          sx={{ justifyContent: "space-between", minHeight: "70px!important" }}
        >
          {/* Logo Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ display: { lg: "none" }, color: "white" }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Link
              to="/"
              style={{
                textDecoration: "none",
                display: "flex",
                background: "white",
                borderRadius: "10px",
                alignItems: "center",
                gap: 16,
                padding: "8px 16px",
              }}
            >
              <img
                width="100"
                src={require("../../images/LUMS.png")}
                alt="LUMS Logo"
                style={{ height: "auto" }}
              />

              {/* Vertical Divider */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  backgroundColor: "#656161",
                  height: 50,
                  width: "1px",
                }}
              />

              <img
                width="40"
                src={require("../../images/wwf_logo.png")}
                alt="WWF Logo"
                style={{ height: "auto" }}
              />
            </Link>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Dark Mode Toggle */}
              <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"} arrow>
                <IconButton
                  onClick={() => setDarkMode(!darkMode)}
                  sx={{
                    ...iconButtonStyle,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.2)",
                    },
                  }}
                >
                  {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>

              {/* Notification Button */}
              <Tooltip title="Notifications" arrow>
                <IconButton
                  onClick={handleNotificationsOpen}
                  sx={{ ...iconButtonStyle, position: "relative" }}
                >
                  <NotificationsIcon />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#ff6b6b",
                    }}
                  />
                </IconButton>
              </Tooltip>

              {/* Organization Chip */}
              {organizationName && (
                <Chip
                  label={organizationName}
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600,
                    borderRadius: "20px",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.3)",
                    },
                  }}
                />
              )}

              {/* Profile Button */}
              <Tooltip title="Account" arrow>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  sx={iconButtonStyle}
                  aria-controls="profile-menu"
                  aria-haspopup="true"
                >
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      width: 40,
                      height: 40,
                      transition: "all 0.3s ease",
                    }}
                  >
                    <AccountCircleIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* Dark Mode Toggle */}
              <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"} arrow>
                <IconButton
                  onClick={() => setDarkMode(!darkMode)}
                  sx={{ ...iconButtonStyle }}
                >
                  {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Notifications" arrow>
                <IconButton
                  onClick={handleNotificationsOpen}
                  sx={{ ...iconButtonStyle }}
                >
                  <NotificationsIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Menu" arrow>
                <IconButton
                  onClick={() => setMobileDrawerOpen(true)}
                  sx={iconButtonStyle}
                >
                  <MenuIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Profile Dropdown Menu */}
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionComponent={Fade}
        PaperProps={{ sx: menuPaperStyle }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ p: 2, textAlign: "center", bgcolor: "#f8f9fa" }}>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mx: "auto",
              mb: 1,
              bgcolor: "#454545",
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#333" }}
          >
            {username}
          </Typography>
          {organizationName && (
            <Typography
              variant="caption"
              sx={{ color: "#666", display: "block" }}
            >
              {organizationName}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: "#666" }}>
            Role: {user.user_type_display || user.user_type || "User"}
          </Typography>
        </Box>
        <Divider />

        <MenuItem onClick={handleProfile} sx={menuItemStyle}>
          <AccountCircleIcon sx={{ mr: 2, fontSize: 20, color: "#454545" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            My Profile
          </Typography>
        </MenuItem>

        {(user.user_type === "admin" || user.user_type === "superuser") && (
          <MenuItem onClick={handleAdminClick} sx={menuItemStyle}>
            <AdminIcon sx={{ mr: 2, fontSize: 20, color: "#454545" }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Admin Panel
            </Typography>
          </MenuItem>
        )}

        <MenuItem onClick={handleChangePassword} sx={menuItemStyle}>
          <SettingsIcon sx={{ mr: 2, fontSize: 20, color: "#454545" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Change Password
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleLogout}
          sx={{
            ...menuItemStyle,
            "&:hover": { backgroundColor: "#fff5f5" },
          }}
        >
          <LogoutIcon sx={{ mr: 2, fontSize: 20, color: "#e74c3c" }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#e74c3c" }}
          >
            Logout
          </Typography>
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 320,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid #eee`,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
        </Box>
        <MenuItem>
          <Typography
            variant="body2"
            sx={{
              p: 2,
              textAlign: "center",
              color: "text.secondary",
              width: "100%",
            }}
          >
            No new notifications
          </Typography>
        </MenuItem>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{ sx: mobileDrawerStyle }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            onClick={() => setMobileDrawerOpen(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ textAlign: "center", py: 2 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: "auto",
              mb: 2,
              bgcolor: "rgba(255,255,255,0.2)",
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 50, color: "white" }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "white" }}>
            {username}
          </Typography>
          {organizationName && (
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.7)" }}
            >
              {organizationName}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.6)", display: "block" }}
          >
            Role: {user.user_type_display || user.user_type || "User"}
          </Typography>
        </Box>

        <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", my: 2 }} />

        <List sx={{ px: 2 }}>
          {navItems.map((item) => (
            <ListItem
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: "12px",
                mb: 1,
                cursor: "pointer",
                transition: "all 0.3s ease",
                bgcolor: isActive(item.path)
                  ? "rgba(255,255,255,0.15)"
                  : "transparent",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.1)",
                  transform: "translateX(8px)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "white", minWidth: "40px" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  sx: { fontWeight: 600, color: "white" },
                }}
              />
            </ListItem>
          ))}

          {(user.user_type === "admin" || user.user_type === "superuser") && (
            <>
              <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", my: 2 }} />
              <ListItem
                onClick={handleAdminClick}
                sx={{
                  borderRadius: "12px",
                  mb: 1,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                    transform: "translateX(8px)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "white", minWidth: "40px" }}>
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Admin Panel"
                  primaryTypographyProps={{
                    sx: { fontWeight: 600, color: "white" },
                  }}
                />
              </ListItem>
            </>
          )}

          <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", my: 2 }} />

          <ListItem
            onClick={handleChangePassword}
            sx={{
              borderRadius: "12px",
              mb: 1,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.1)",
                transform: "translateX(8px)",
              },
            }}
          >
            <ListItemIcon sx={{ color: "white", minWidth: "40px" }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Change Password"
              primaryTypographyProps={{
                sx: { fontWeight: 600, color: "white" },
              }}
            />
          </ListItem>

          <ListItem
            onClick={handleLogout}
            sx={{
              borderRadius: "12px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "rgba(231,76,60,0.2)",
                transform: "translateX(8px)",
              },
            }}
          >
            <ListItemIcon sx={{ color: "#ff6b6b", minWidth: "40px" }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                sx: { fontWeight: 700, color: "#ff6b6b" },
              }}
            />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
